import React, { useState, useEffect, useRef } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, Download, Plus, Minus, X, Edit } from 'lucide-react';

// Mock data for initial chart display with nonResourceDependencies field added
const initialData = {
  "ShipMaintenance": [
    { id: 1, name: "Project information", duration: 3, dependencies: [], nonResourceDependencies: [], x: 0, y: 1 },
    { id: 2, name: "Communication setup", duration: 5, dependencies: [1], nonResourceDependencies: [], x: 1, y: 0 },
    { id: 3, name: "Hull inspection", duration: 7, dependencies: [1], nonResourceDependencies: [], x: 1, y: 1 },
    { id: 4, name: "Material procurement", duration: 10, dependencies: [1], nonResourceDependencies: [], x: 1, y: 2 },
    { id: 5, name: "Design approval", duration: 8, dependencies: [2], nonResourceDependencies: [], x: 2, y: 0 },
    { id: 6, name: "Dry docking", duration: 12, dependencies: [3], nonResourceDependencies: [5], x: 2, y: 1 },
    { id: 7, name: "Hull repair", duration: 15, dependencies: [4], nonResourceDependencies: [], x: 2, y: 2 },
    { id: 8, name: "System testing", duration: 6, dependencies: [5], nonResourceDependencies: [], x: 3, y: 0 },
    { id: 9, name: "Quality control", duration: 4, dependencies: [7], nonResourceDependencies: [], x: 3, y: 2 },
    { id: 10, name: "Project completion", duration: 3, dependencies: [6, 8], nonResourceDependencies: [9], x: 3, y: 1 },
  ]
};

// Color palette to match the example
const colorPalette = [
  'bg-orange-400', // Coral
  'bg-blue-500',   // Blue
  'bg-purple-800', // Purple
  'bg-green-500',  // Green
  'bg-orange-400', // Coral (repeating)
  'bg-blue-500',   // Blue (repeating)
  'bg-purple-800', // Purple (repeating)
];

export default function ShipyardPertChart() {
  const [projects, setProjects] = useState(Object.keys(initialData));
  const [selectedProject, setSelectedProject] = useState(projects[0] || '');
  const [chartData, setChartData] = useState(selectedProject ? initialData[selectedProject] : []);
  const [zoomLevel, setZoomLevel] = useState(100);
  const chartContainerRef = useRef(null);
  
  // New project modal state
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [tasks, setTasks] = useState([
    { id: 1, name: '', duration: 0, dependencies: [], nonResourceDependencies: [], x: 0, y: 0 }
  ]);

  // Edit project modal state
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  // Handle project change
  const handleProjectChange = (event) => {
    const newProject = event.target.value;
    setSelectedProject(newProject);
    // Make sure the project exists in initialData before setting chartData
    if (initialData[newProject]) {
      setChartData(initialData[newProject]);
    } else {
      // Set to empty array if project doesn't exist
      setChartData([]);
      console.error(`Project "${newProject}" not found in data`);
    }
  };

  // Open edit project modal
  const openEditProjectModal = (projectName) => {
    setEditingProject(projectName);
    setNewProjectName(projectName);
    setTasks(initialData[projectName] || []);
    setShowEditProjectModal(true);
  };

  // Close edit project modal
  const closeEditProjectModal = () => {
    setShowEditProjectModal(false);
    setEditingProject(null);
  };

  // Save edited project
  const saveEditedProject = () => {
    if (!newProjectName.trim()) {
      alert("Please enter a project name");
      return;
    }
    
    // Validate that all tasks have names
    if (tasks.some(task => !task.name.trim())) {
      alert("All tasks must have names");
      return;
    }
    
    // Remove the old project if the name changed
    if (editingProject !== newProjectName) {
      delete initialData[editingProject];
      
      // Update projects list
      const updatedProjects = projects.filter(p => p !== editingProject);
      setProjects([...updatedProjects, newProjectName]);
    }
    
    // Save the edited project
    initialData[newProjectName] = tasks;
    
    // Select the edited project
    setSelectedProject(newProjectName);
    setChartData(tasks);
    
    // Close the modal
    closeEditProjectModal();
  };

  // Convert chart data to CSV
  const exportToCSV = () => {
    // Check if chartData exists before proceeding
    if (!chartData || chartData.length === 0) {
      console.error("No chart data available to export");
      return;
    }

    const headers = ['id', 'name', 'duration', 'dependencies', 'nonResourceDependencies', 'x', 'y'];
    const csvRows = [
      headers.join(','),
      ...chartData.map(task => {
        return [
          task.id,
          task.name,
          task.duration,
          task.dependencies.join('|'),
          task.nonResourceDependencies.join('|'),
          task.x,
          task.y
        ].join(',');
      })
    ];
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedProject}_pert_chart.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Import CSV file
  const importCSV = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const rows = text.split('\n').filter(row => row.trim() !== ''); // Filter out empty rows
        
        if (rows.length < 2) {
          console.error("CSV file is empty or invalid");
          return;
        }

        const headers = rows[0].split(',');
        
        const importedData = rows.slice(1).map(row => {
          const values = row.split(',');
          const task = {};
          
          headers.forEach((header, index) => {
            if (header === 'dependencies' || header === 'nonResourceDependencies') {
              task[header] = values[index] ? values[index].split('|').map(Number) : [];
            } else if (['id', 'x', 'y', 'duration'].includes(header)) {
              task[header] = Number(values[index]);
            } else {
              task[header] = values[index];
            }
          });
          
          return task;
        });
        
        // Check if this is a new project or update to existing one
        const projectName = file.name.replace('.csv', '');
        
        // Update initialData
        initialData[projectName] = importedData;
        
        // Update projects list if it's a new project
        if (!projects.includes(projectName)) {
          setProjects([...projects, projectName]);
        }
        
        setSelectedProject(projectName);
        setChartData(importedData);
      } catch (error) {
        console.error("Error importing CSV:", error);
        // Set chartData to empty array in case of error
        setChartData([]);
        alert("Failed to import CSV. Please check the file format.");
      }
    };
    
    reader.readAsText(file);
  };

  // Open new project modal
  const openNewProjectModal = () => {
    setNewProjectName('');
    setTasks([{ id: 1, name: '', duration: 0, dependencies: [], nonResourceDependencies: [], x: 0, y: 0 }]);
    setShowNewProjectModal(true);
  };

  // Close new project modal
  const closeNewProjectModal = () => {
    setShowNewProjectModal(false);
  };

  // Handle task changes
  const handleTaskChange = (index, field, value) => {
    const updatedTasks = [...tasks];
    
    if (field === 'dependencies' || field === 'nonResourceDependencies') {
      // Convert comma-separated string to array of numbers
      updatedTasks[index][field] = value ? value.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id)) : [];
    } else if (['id', 'duration', 'x', 'y'].includes(field)) {
      updatedTasks[index][field] = Number(value);
    } else {
      updatedTasks[index][field] = value;
    }
    
    setTasks(updatedTasks);
  };

  // Add new task
  const addTask = () => {
    const newId = tasks.length > 0 ? Math.max(...tasks.map(t => t.id)) + 1 : 1;
    setTasks([...tasks, { id: newId, name: '', duration: 0, dependencies: [], nonResourceDependencies: [], x: 0, y: 0 }]);
  };

  // Remove task
  const removeTask = (index) => {
    if (tasks.length > 1) {
      const updatedTasks = [...tasks];
      updatedTasks.splice(index, 1);
      setTasks(updatedTasks);
    }
  };

  // Save new project
  const saveNewProject = () => {
    if (!newProjectName.trim()) {
      alert("Please enter a project name");
      return;
    }
    
    // Validate that all tasks have names
    if (tasks.some(task => !task.name.trim())) {
      alert("All tasks must have names");
      return;
    }
    
    // Save the new project
    initialData[newProjectName] = tasks;
    
    // Update projects list
    const updatedProjects = [...projects, newProjectName];
    setProjects(updatedProjects);
    
    // Select the new project
    setSelectedProject(newProjectName);
    setChartData(tasks);
    
    // Close the modal
    closeNewProjectModal();
  };

  // Zoom controls
  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 10, 150));
  };

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 10, 50));
  };

  // Helper to calculate grid positions
  const getGridPositions = () => {
    // Check if chartData exists before proceeding
    if (!chartData || chartData.length === 0) return [];
    
    // Find max x and y values
    const maxX = Math.max(...chartData.map(t => t.x));
    const maxY = Math.max(...chartData.map(t => t.y));
    
    // Create a grid cell for each position
    const grid = [];
    for (let y = 0; y <= maxY; y++) {
      for (let x = 0; x <= maxX; x++) {
        grid.push({ x, y });
      }
    }
    
    return grid;
  };

  // Get task at a specific grid position
  const getTaskAtPosition = (x, y) => {
    if (!chartData) return null;
    return chartData.find(task => task.x === x && task.y === y);
  };

  // Calculate connection paths
  const calculateConnections = () => {
    // Check if chartData exists before proceeding
    if (!chartData || chartData.length === 0) return [];
    
    // Regular dependencies (solid lines)
    const regularDeps = chartData.flatMap(task => {
      if (!task.dependencies || !Array.isArray(task.dependencies)) return [];
      
      return task.dependencies.map(depId => {
        const sourceTask = chartData.find(t => t.id === depId);
        if (!sourceTask) return null;
        
        return {
          from: { x: sourceTask.x, y: sourceTask.y },
          to: { x: task.x, y: task.y },
          dashed: false
        };
      }).filter(Boolean);
    });
    
    // Dependencies without resources (dashed lines)
    const nonResourceDeps = chartData.flatMap(task => {
      if (!task.nonResourceDependencies || !Array.isArray(task.nonResourceDependencies)) return [];
      
      return task.nonResourceDependencies.map(depId => {
        const sourceTask = chartData.find(t => t.id === depId);
        if (!sourceTask) return null;
        
        return {
          from: { x: sourceTask.x, y: sourceTask.y },
          to: { x: task.x, y: task.y },
          dashed: true
        };
      }).filter(Boolean);
    });
    
    return [...regularDeps, ...nonResourceDeps];
  };

  // Effect to set chartData when project changes
  useEffect(() => {
    if (selectedProject && initialData[selectedProject]) {
      setChartData(initialData[selectedProject]);
    } else {
      setChartData([]);
    }
  }, [selectedProject]);

  // Initialize chartData with the first project if it exists
  useEffect(() => {
    if (projects.length > 0 && initialData[projects[0]]) {
      setSelectedProject(projects[0]);
      setChartData(initialData[projects[0]]);
    }
  }, []);

  return (
    <div className="bg-gray-100 min-h-screen max-h-screen overflow-hidden">
      <div className="bg-white rounded-lg shadow-lg p-4 flex flex-col h-screen">
        {/* Header area with controls */}
        <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-800">Shipyard PERT Chart</h1>
          
          <div className="flex space-x-4">
            {/* Add New Project button */}
            <button
              onClick={openNewProjectModal}
              className="flex items-center bg-blue-600 text-white rounded-md px-3 py-2 hover:bg-blue-700"
            >
              <Plus size={18} className="mr-1" />
              <span>Add Project</span>
            </button>
            
            {/* Project selector */}
            <div className="flex items-center">
              <label htmlFor="project-select" className="mr-2 text-gray-700">Project:</label>
              <select 
                id="project-select"
                value={selectedProject}
                onChange={handleProjectChange}
                className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {projects.map(project => (
                  <option key={project} value={project}>{project}</option>
                ))}
              </select>
              <button
                onClick={() => openEditProjectModal(selectedProject)}
                className="ml-2 p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded-md"
                title="Edit Project"
              >
                <Edit size={18} />
              </button>
            </div>
            
            {/* CSV import/export */}
            <div className="flex space-x-2">
              <label className="flex items-center cursor-pointer bg-blue-500 text-white rounded-md px-3 py-2 hover:bg-blue-600">
                <ArrowUpFromLine size={18} className="mr-1" />
                <span>Import CSV</span>
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={importCSV}
                />
              </label>
              
              <button
                onClick={exportToCSV}
                className="flex items-center bg-green-500 text-white rounded-md px-3 py-2 hover:bg-green-600"
              >
                <ArrowDownToLine size={18} className="mr-1" />
                <span>Export CSV</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Main chart area */}
        <div 
          ref={chartContainerRef}
          className="flex-1 overflow-hidden bg-gray-50 rounded-lg relative"
          style={{ transform: `scale(${zoomLevel/100})`, transformOrigin: 'center center' }}
        >
          {/* PERT Chart Grid */}
          <div className="absolute inset-0 flex items-start justify-center">
            <div className="relative">
              {/* Draw connections first (arrows) */}
              <svg className="absolute inset-0" width="1000" height="600" style={{ zIndex: 0 }}>
                {calculateConnections().map((conn, idx) => {
                  const startX = 200 + conn.from.x * 200 + 80;
                  const startY = 150 + conn.from.y * 150 + 40;
                  const endX = 200 + conn.to.x * 200 + 20;
                  const endY = 150 + conn.to.y * 150 + 40;
                  
                  // Calculate arrow marker properties
                  const dx = endX - startX;
                  const dy = endY - startY;
                  const angle = Math.atan2(dy, dx) * 180 / Math.PI;
                  
                  // Move end point slightly to accommodate arrow head
                  const shortenedEndX = endX - 10 * Math.cos(angle * Math.PI / 180);
                  const shortenedEndY = endY - 10 * Math.sin(angle * Math.PI / 180);
                  
                  return (
                    <g key={idx}>
                      <line 
                        x1={startX} y1={startY} 
                        x2={shortenedEndX} y2={shortenedEndY}
                        stroke="black" 
                        strokeWidth={2}
                        strokeDasharray={conn.dashed ? "5,5" : "none"}
                      />
                      {/* Arrow head */}
                      <polygon 
                        points={`${endX},${endY} ${endX-12},${endY-6} ${endX-12},${endY+6}`}
                        transform={`rotate(${angle} ${endX} ${endY})`}
                        fill="black"
                      />
                    </g>
                  );
                })}
              </svg>
              
              {/* Task nodes */}
              <div className="relative" style={{ width: '1000px', height: '600px' }}>
                {chartData && chartData.length > 0 ? (
                  chartData.map((task) => {
                    const leftPos = 200 + task.x * 200;
                    const topPos = 150 + task.y * 150;
                    const colorClass = colorPalette[task.id % colorPalette.length];
                    
                    return (
                      <div
                        key={task.id}
                        className={`absolute ${colorClass} rounded-lg shadow-md p-3 w-40 text-white`}
                        style={{ 
                          left: `${leftPos}px`, 
                          top: `${topPos}px`,
                          zIndex: 1
                        }}
                      >
                        <div className="text-sm font-bold">{task.id}</div>
                        <div className="font-medium">{task.name}</div>
                        <div className="text-xs mt-1">Duration: {task.duration} days</div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-gray-500">
                    No chart data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Legend */}
        <div className="pt-3 mt-3 border-t border-gray-200">
          <div className="flex items-center space-x-8">
            <div className="flex items-center">
              <div className="w-12 h-6 bg-white border border-gray-300 rounded-md mr-2"></div>
              <span className="text-sm">Tasks</span>
            </div>
            <div className="flex items-center">
              <div className="flex items-center">
                <svg width="50" height="20">
                  <line x1="5" y1="10" x2="35" y2="10" stroke="black" strokeWidth="2" />
                  <polygon points="45,10 35,5 35,15" fill="black" />
                </svg>
                <span className="text-sm">Dependencies</span>
              </div>
            </div>
            <div className="flex items-center">
              <div className="flex items-center">
                <svg width="50" height="20">
                  <line x1="5" y1="10" x2="35" y2="10" stroke="black" strokeWidth="2" strokeDasharray="5,5" />
                  <polygon points="45,10 35,5 35,15" fill="black" />
                </svg>
                <span className="text-sm">Dependencies without resources</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* New Project Modal */}
        {showNewProjectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-4/5 max-w-4xl max-h-screen overflow-y-auto">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-bold">Add New Project</h2>
                <button 
                  onClick={closeNewProjectModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-4">
                {/* Project Name */}
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-2">Project Name</label>
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    className="border border-gray-300 rounded-md p-2 w-full"
                    placeholder="Enter project name"
                  />
                </div>
                
                {/* Tasks */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-gray-700 font-medium">Tasks</label>
                    <button
                      onClick={addTask}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <Plus size={16} className="mr-1" />
                      <span>Add Task</span>
                    </button>
                  </div>
                  
                  {/* Task Table */}
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-2 text-left">ID</th>
                          <th className="p-2 text-left">Name</th>
                          <th className="p-2 text-left">Duration (days)</th>
                          <th className="p-2 text-left">Dependencies</th>
                          <th className="p-2 text-left">Non-Resource Dependencies</th>
                          <th className="p-2 text-left">X</th>
                          <th className="p-2 text-left">Y</th>
                          <th className="p-2 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((task, index) => (
                          <tr key={index} className="border-t border-gray-200">
                            <td className="p-2">
                              <input
                                type="number"
                                value={task.id}
                                onChange={(e) => handleTaskChange(index, 'id', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-12"
                                min="1"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                value={task.name}
                                onChange={(e) => handleTaskChange(index, 'name', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-full"
                                placeholder="Task name"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                value={task.duration}
                                onChange={(e) => handleTaskChange(index, 'duration', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-20"
                                min="0"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                value={task.dependencies.join(', ')}
                                onChange={(e) => handleTaskChange(index, 'dependencies', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-24"
                                placeholder="1, 2, 3"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                value={task.nonResourceDependencies.join(', ')}
                                onChange={(e) => handleTaskChange(index, 'nonResourceDependencies', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-24"
                                placeholder="4, 5, 6"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                value={task.x}
                                onChange={(e) => handleTaskChange(index, 'x', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-12"
                                min="0"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                value={task.y}
                                onChange={(e) => handleTaskChange(index, 'y', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-12"
                                min="0"
                              />
                            </td>
                            <td className="p-2">
                              <button
                                onClick={() => removeTask(index)}
                                className="text-red-600 hover:text-red-800"
                                disabled={tasks.length === 1}
                              >
                                <X size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={closeNewProjectModal}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md mr-2"
                >
                  Cancel
                </button>
                <button
                  onClick={saveNewProject}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save Project
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Project Modal */}
        {showEditProjectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-4/5 max-w-4xl max-h-screen overflow-y-auto">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-bold">Edit Project: {editingProject}</h2>
                <button 
                  onClick={closeEditProjectModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-4">
                {/* Project Name */}
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-2">Project Name</label>
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    className="border border-gray-300 rounded-md p-2 w-full"
                    placeholder="Enter project name"
                  />
                </div>
                
                {/* Tasks */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-gray-700 font-medium">Tasks</label>
                    <button
                      onClick={addTask}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <Plus size={16} className="mr-1" />
                      <span>Add Task</span>
                    </button>
                  </div>
                  
                  {/* Task Table */}
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-2 text-left">ID</th>
                          <th className="p-2 text-left">Name</th>
                          <th className="p-2 text-left">Duration (days)</th>
                          <th className="p-2 text-left">Dependencies</th>
                          <th className="p-2 text-left">Non-Resource Dependencies</th>
                          <th className="p-2 text-left">X</th>
                          <th className="p-2 text-left">Y</th>
                          <th className="p-2 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((task, index) => (
                          <tr key={index} className="border-t border-gray-200">
                            <td className="p-2">
                              <input
                                type="number"
                                value={task.id}
                                onChange={(e) => handleTaskChange(index, 'id', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-12"
                                min="1"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                value={task.name}
                                onChange={(e) => handleTaskChange(index, 'name', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-full"
                                placeholder="Task name"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                value={task.duration}
                                onChange={(e) => handleTaskChange(index, 'duration', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-20"
                                min="0"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                value={task.dependencies.join(', ')}
                                onChange={(e) => handleTaskChange(index, 'dependencies', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-24"
                                placeholder="1, 2, 3"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="text"
                                value={task.nonResourceDependencies.join(', ')}
                                onChange={(e) => handleTaskChange(index, 'nonResourceDependencies', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-24"
                                placeholder="4, 5, 6"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                value={task.x}
                                onChange={(e) => handleTaskChange(index, 'x', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-12"
                                min="0"
                              />
                            </td>
                            <td className="p-2">
                              <input
                                type="number"
                                value={task.y}
                                onChange={(e) => handleTaskChange(index, 'y', e.target.value)}
                                className="border border-gray-300 rounded-md p-1 w-12"
                                min="0"
                              />
                            </td>
                            <td className="p-2">
                              <button
                                onClick={() => removeTask(index)}
                                className="text-red-600 hover:text-red-800"
                                disabled={tasks.length === 1}
                              >
                                <X size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={closeEditProjectModal}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md mr-2"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEditedProject}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}